import { AfterViewChecked, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';

import { CatalogObject, Property, PropertySet, PropertyType } from '@mirage-projects/cw-models';

import { PropertyBagService } from './property-bag.service';

@Component({
    selector: 'cw-property-bag',
    templateUrl: './property-bag.component.html',
    styleUrls: ['./property-bag.component.scss'],
    standalone: false,
})
export class PropertyBagComponent implements OnInit, AfterViewChecked {
    /** This object comes from the core and provides methods to obtain and set the value of the properties */
    @Input() propertySet!: PropertySet;

    /** Set of fields that will compose the property bag */
    @Input() properties: Property[] = [];

    @Input() catalogObject: CatalogObject | undefined;

    constructor(
        private propertyBagService: PropertyBagService,
        private cdr: ChangeDetectorRef
    ) {}
    ngAfterViewChecked(): void {
        this.cdr.detectChanges();
    }

    ngOnInit(): void {
        // If a property does not have a specified order, we will paint it at the end
        this.properties.sort((p1, p2) => (p1.sortIndex ?? 99999) - (p2.sortIndex ?? 99999));
    }

    isGroup(property: Property): boolean {
        return property.type === PropertyType.GroupField;
    }

    isObject(property: Property): boolean {
        return property.type === PropertyType.Object;
    }

    isVisible(property: Property): boolean {
        return property.visible ? property.visible() : true;
    }

    isVisibleGroup(property: Property): boolean {
        if (!this.isVisible(property)) {
            return false;
        }
        if (this.isGroup(property) || this.isGeometry(property)) {
            return true;
        }
        if (this.isObject(property)) {
            return !!this.propertySet.getProperty(property.field);
        }
        return false;
    }

    isGeometry(property: Property): boolean {
        return property.type === PropertyType.GeometryField;
    }

    isPointArray(property: Property): boolean {
        return property.type === PropertyType.PointArrayField;
    }

    isColor(property: Property): boolean {
        return property.type === PropertyType.ColorField;
    }

    isBoolean(property: Property): boolean {
        return property.type === PropertyType.BooleanField;
    }

    isToggle(property: Property): boolean {
        return property.type === PropertyType.ToggleField;
    }

    isPoint(property: Property): boolean {
        return property.type === PropertyType.Point;
    }

    isNumber(property: Property): boolean {
        return property.type === PropertyType.IntegerField || property.type === PropertyType.FloatField;
    }

    isSelect(property: Property): boolean {
        return property.type === PropertyType.CharField && this.propertyBagService.getValueRange(this.propertySet, property)?.length > 0;
    }

    isText(property: Property): boolean {
        return (
            (property.type === PropertyType.CharField || property.type === PropertyType.TextField) &&
            !this.propertyBagService.getValueRange(this.propertySet, property)
        );
    }

    isExpression(property: Property): boolean {
        return property.type === PropertyType.ExpressionField;
    }

    isSwitch(property: Property): boolean {
        return property.type === PropertyType.SwitchField;
    }

    isClassification(property: Property): boolean {
        return property.type === PropertyType.ClassificationField;
    }
}

/**
 * This component shows a group of property with a accordion
 */
@Component({
    selector: 'cw-property-group',
    templateUrl: './property-group/property-group.component.html',
    styleUrls: ['./property-group/property-group.component.scss'],
    standalone: false,
})
export class PropertyGroupComponent implements OnInit {
    /** This object comes from the core and provides methods to obtain and set the value of the properties */
    @Input() propertySet!: PropertySet;
    /** Input field with the required information for this field type */
    @Input() property!: Property;
    /** Catalog object data */
    @Input() catalogObject: CatalogObject | undefined;

    childPropertySet!: PropertySet;
    childrenFields: Property[] = [];
    propertyBagService = inject(PropertyBagService);
    label = '';

    ngOnInit(): void {
        this.label = this.propertyBagService.getCorrectLabel(this.property);
        this.initializeChildren();
    }

    initializeChildren(): void {
        if (this.isGroup()) {
            this.childPropertySet = this.propertySet;
            this.childrenFields = this.property.children ?? [];
        } else {
            this.childPropertySet = this.propertyBagService.getValueProperty(this.propertySet, this.property);
            if (!this.childPropertySet) {
                return;
            }
            this.childPropertySet.initializePropertySet(this.catalogObject);
            this.childrenFields = this.childPropertySet.getProperties();
        }
    }

    isGroup(): boolean {
        return this.property.type === PropertyType.GroupField;
    }

    isObject(): boolean {
        return this.property.type === PropertyType.Object;
    }

    isExpanded(): boolean {
        return this.property.expanded === false ? false : true;
    }

    isRule(): boolean {
        return this.property.field.indexOf('ruleGroup') === 0;
    }

    isStyle(): boolean {
        return this.property.field === 'styleGroup' && !!this.propertySet.getGeometryType;
    }

    isGeometry(): boolean {
        return this.property.type === PropertyType.GeometryField;
    }

    getGeometryName(): string {
        const valueGeometry = this.propertyBagService.getValueProperty(this.propertySet, this.property);
        return valueGeometry.getGeometryName();
    }

    deleteRule(event: Event): void {
        event.stopPropagation();
        this.propertyBagService.deleteRule(this.propertySet, this.property);
    }

    addRule(event: Event): void {
        event.stopPropagation();
        this.propertyBagService.addRule(this.propertySet);
    }
}
